import ChartFrame from './ChartFrame'
import DataTable from '../ui/DataTable'
import { formatPercent, sharePercent } from '../lib/format'
import type { DnsechoRow } from '@shared/api'
import styles from './ShareBars.module.css'

interface ShareBarsProps {
    rows: DnsechoRow[]
}

/**
 * Share of observed queries per resolver, sorted descending.
 *
 * Single hue throughout, deliberately: these are nominal categories -- IP
 * addresses have no inherent order -- so colouring by value would spend the
 * identity channel restating what bar length already says.
 */
export default function ShareBars({ rows }: ShareBarsProps) {
    const total = rows.reduce((sum, r) => sum + r.count, 0)
    const sorted = [...rows].sort((a, b) => b.count - a.count)
    const max = sorted[0]?.count ?? 1

    return (
        <ChartFrame
            title="Query share"
            aside={`${total} queries observed`}
            table={
                <DataTable
                    caption="Share of observed queries per resolver"
                    columns={[
                        { key: 'ip', header: 'Resolver', render: (r: DnsechoRow) => r.ip },
                        { key: 'count', header: 'Queries', numeric: true, render: (r: DnsechoRow) => r.count },
                        { key: 'share', header: 'Share', numeric: true,
                          render: (r: DnsechoRow) => formatPercent(sharePercent(r.count, total)) },
                    ]}
                    rows={sorted}
                    rowKey={(r) => r.ip}
                />
            }
        >
            <ul className={styles.list}>
                {sorted.map((row) => {
                    const pct = sharePercent(row.count, total)
                    return (
                        <li key={row.ip} className={styles.row}>
                            <span className={styles.label}>{row.ip}</span>
                            <span className={styles.track}>
                                <span
                                    className={styles.bar}
                                    style={{ width: `${(row.count / max) * 100}%` }}
                                />
                            </span>
                            <span className={styles.value}>{formatPercent(pct)}</span>
                        </li>
                    )
                })}
            </ul>
        </ChartFrame>
    )
}
